import { cx } from "../../lib/cx.js";
import { Icon } from "../Icon/Icon.js";
import { Tooltip } from "../Tooltip/Tooltip.js";
import { useOptionalBrand } from "../ThemeRoot/BrandContext.js";
import { useAppShell } from "../AppShell/AppShellContext.js";
import { BrandMark } from "../BrandMark/BrandMark.js";
/* empty css            */
import { createContext, isValidElement, useContext, useId } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/Sidebar/Sidebar.tsx
/** Whether the sidebar is collapsed to its icon rail, for the items inside. */
var CollapsedContext = createContext(false);
/**
* The navigation sidebar: an optional brand header, a `nav` of items and sections, and
* a footer. Inside an AppShell it follows the shell: collapsed to an icon rail on
* desktop, and inside the menu drawer when narrow, where choosing an item closes it.
*/
function Sidebar({ brand, productName: productNameProp, children, footer, collapsed: collapsedProp, inverse: inverseProp, "aria-label": ariaLabel = "Main", className, ...rest }) {
	const shell = useAppShell();
	const brandConfig = useOptionalBrand();
	const brandOnTop = shell?.brandPlacement === "sidebar";
	const collapsed = collapsedProp ?? shell?.collapsed ?? false;
	const inverse = inverseProp ?? shell?.sidebarSurface === "inverse";
	const brandNode = brand === void 0 ? brandOnTop && brandConfig ? /* @__PURE__ */ jsx(BrandMark, { size: "sm" }) : null : brand;
	const productName = productNameProp === void 0 ? brandNode && brandConfig?.productName : productNameProp;
	return /* @__PURE__ */ jsxs("div", {
		...rest,
		className: cx("ds-sidebar", collapsed && "ds-sidebar--collapsed", className),
		"data-surface": inverse ? "inverse" : void 0,
		children: [
			(brandNode || productName) && /* @__PURE__ */ jsxs("div", {
				className: "ds-sidebar__brand",
				children: [brandNode, productName && /* @__PURE__ */ jsx("span", {
					className: "ds-sidebar__product",
					children: productName
				})]
			}),
			/* @__PURE__ */ jsx("nav", {
				className: "ds-sidebar__nav",
				"aria-label": ariaLabel,
				children: /* @__PURE__ */ jsx(CollapsedContext.Provider, {
					value: collapsed,
					children: /* @__PURE__ */ jsx("ul", {
						className: "ds-sidebar__list",
						children
					})
				})
			}),
			footer && /* @__PURE__ */ jsx("div", {
				className: "ds-sidebar__footer",
				children: footer
			})
		]
	});
}
/** A labelled group of SidebarItems. In the rail its heading is hidden, but still read. */
function SidebarSection({ label, icon, children, className, ...rest }) {
	const id = useId();
	return /* @__PURE__ */ jsxs("li", {
		...rest,
		className: cx("ds-sidebar__section", className),
		children: [/* @__PURE__ */ jsxs("span", {
			id,
			className: "ds-sidebar__heading",
			children: [icon && /* @__PURE__ */ jsx(ItemIcon, { icon }), /* @__PURE__ */ jsx("span", {
				className: "ds-sidebar__heading-label",
				children: label
			})]
		}), /* @__PURE__ */ jsx("ul", {
			className: "ds-sidebar__list",
			"aria-labelledby": id,
			children
		})]
	});
}
function ItemIcon({ icon }) {
	return /* @__PURE__ */ jsx("span", {
		className: "ds-sidebar__icon",
		"aria-hidden": "true",
		children: isValidElement(icon) ? icon : /* @__PURE__ */ jsx(Icon, { name: icon })
	});
}
/**
* A nav item: an icon, the label, and a count or other trailing content. A link with
* `href` (or `as`), else a button. Choosing it closes the AppShell's menu drawer.
*/
function SidebarItem({ children, icon, active = false, count, countLabel, trailing, href, onClick, as, disabled = false, className, ...rest }) {
	const shell = useAppShell();
	const collapsed = useContext(CollapsedContext);
	const isLink = as !== void 0 || href !== void 0;
	const Element = as ?? (href !== void 0 ? "a" : "button");
	const handleClick = (event) => {
		if (disabled) {
			event.preventDefault();
			return;
		}
		onClick?.(event);
		shell?.closeMenu();
	};
	const linkProps = isLink ? {
		href: disabled ? void 0 : href,
		"aria-disabled": disabled || void 0
	} : {
		type: "button",
		disabled
	};
	const hasCount = count !== void 0 && count !== "";
	const item = /* @__PURE__ */ jsxs(Element, {
		...rest,
		...linkProps,
		className: cx("ds-sidebar__item", active && "ds-sidebar__item--active", className),
		"aria-current": active ? "page" : void 0,
		onClick: handleClick,
		children: [
			icon && /* @__PURE__ */ jsx(ItemIcon, { icon }),
			/* @__PURE__ */ jsx("span", {
				className: "ds-sidebar__label",
				children
			}),
			hasCount && " ",
			hasCount && /* @__PURE__ */ jsxs("span", {
				className: "ds-sidebar__count",
				children: [/* @__PURE__ */ jsx("span", {
					"aria-hidden": countLabel ? true : void 0,
					children: count
				}), countLabel && /* @__PURE__ */ jsx("span", {
					className: "ds-sidebar__sr",
					children: countLabel
				})]
			}),
			!hasCount && trailing && /* @__PURE__ */ jsx("span", {
				className: "ds-sidebar__trailing",
				children: trailing
			})
		]
	});
	return /* @__PURE__ */ jsx("li", {
		className: "ds-sidebar__entry",
		children: collapsed ? /* @__PURE__ */ jsx(Tooltip, {
			content: children,
			placement: "right",
			describes: false,
			children: item
		}) : item
	});
}
//#endregion
export { Sidebar, SidebarItem, SidebarSection };

//# sourceMappingURL=Sidebar.js.map